import { PRICING } from '@workspace/seedforge-shared'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { cn } from '@workspace/ui/lib/utils'
import { ExternalLink, Eye, ShieldCheck, UserPlus } from 'lucide-react'
import { useStartupContext } from '../../../contexts/startup-guard'
import { useStartupMatchesQuery } from '../../../hooks/api'
import { deriveContactsToReach } from '../../../lib/matches'
import { useEnableOutreachMutation } from '../hooks/use-enable-outreach-mutation'
import { OutreachAcquisitionHero } from './outreach-acquisition-hero'
import { OutreachAcquisitionLaunchBar } from './outreach-acquisition-launch-bar'
import { OutreachActivationStepsPreview } from './outreach-activation-steps-preview'
import { OutreachTrustRow } from './outreach-trust-row'

/**
 * Coming-soon landing shown to founders whose startup does not yet have
 * OUTREACH feature flag access. Mirrors the dashboard acquisition view (same
 * Hero / Pricing / Trust / Launch Bar) with CTAs rendered as disabled
 * "Coming soon" tiles, plus a static how-it-works demo so the founder can
 * see concrete value while waiting.
 *
 * Intentionally does NOT enumerate the founder's matched investors here —
 * the named preview belongs on /investors and the dashboard, not on a gated
 * teaser. Aggregate counts in the Hero are kept so the value statement is
 * still concrete.
 */
/**
 * A refused start is almost always the OUTREACH gate rather than a fault, and
 * "try again" is bad advice for a permission the founder cannot grant himself.
 * `request` throws `Error("<status> <statusText>: <body>")`, so the status is
 * read off the front of the message.
 */
function startErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : ''
  if (message.startsWith('403')) {
    return "Outreach isn't switched on for your account yet. Talk to us and we'll open it up."
  }
  return "Couldn't start outreach. Please try again."
}

export function OutreachComingSoon({ startupId }: { startupId: string }) {
  const { investorMatchesUnlocked } = useStartupContext()
  // Seeing this page is the permission. There is no separate "can launch"
  // state: a founder who reached it can start from it, and enabling is what
  // provisions the workspace and moves them to the real dashboard.
  const enableOutreach = useEnableOutreachMutation(startupId)
  const matchesQuery = useStartupMatchesQuery(startupId, 50)
  // The read endpoints skip the OUTREACH gate but `enable` does not, so this
  // page is reachable by founders whose start will be refused. Without a
  // message the button just returns from "Starting…" to idle and the founder
  // is left clicking a button that appears to do nothing.
  const startError = enableOutreach.isError ? startErrorMessage(enableOutreach.error) : null

  if (matchesQuery.isLoading) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
        <LoadingSpinner size="sm" />
      </div>
    )
  }

  // Mirror the dashboard's filtering rule so coming-soon counts match what the
  // founder sees on /app/startup/$id and /investors.
  const visibleMatches = (matchesQuery.data?.data?.matches ?? []).filter(
    (m) => investorMatchesUnlocked || m.source !== 'paid_eval'
  )
  const matchesCount = matchesQuery.data?.data?.total ?? visibleMatches.length
  // Contacts are filled in per-fund by activity research; until every matched
  // fund is researched we show a per-fund estimate rather than a low count.
  const { count: totalContacts, estimated: contactsEstimated } = deriveContactsToReach(
    matchesCount,
    matchesQuery.data?.data?.contactReach,
    visibleMatches.reduce((sum, m) => sum + m.investorContacts.length, 0)
  )

  return (
    <div className="mx-auto max-w-6xl py-6">
      <OutreachAcquisitionHero
        matchesCount={matchesCount}
        contactsCount={totalContacts}
        contactsEstimated={contactsEstimated}
        noMatches={matchesCount === 0}
        onStart={() => enableOutreach.mutate()}
        isStarting={enableOutreach.isPending}
        startError={startError}
      />

      <OutreachActivationStepsPreview />

      <HowItWorks />

      <OutreachTrustRow />

      {/* The cost breakdown above already states the prices, in the context of
          a real conversation. Repeating them as a three-cell table said the
          same thing twice, and the two could drift apart. What does not appear
          up there is the wallet terms, so that line stays. */}
      <section className="mb-10">
        <div className="flex items-center justify-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-4 py-2.5 text-[13.5px] font-medium text-emerald-400">
          <ShieldCheck className="size-4" />
          First month free. Then ${PRICING.outreach.minDeposit} top up to continue, refundable
          anytime.
        </div>
      </section>

      <OutreachAcquisitionLaunchBar
        noMatches={matchesCount === 0}
        onStart={() => enableOutreach.mutate()}
        isStarting={enableOutreach.isPending}
        startError={startError}
      />
    </div>
  )
}

// ─── How it works (chat demo + cost breakdown) ──────────────────────────────

function HowItWorks() {
  return (
    <section className="mb-10">
      <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        How it works
      </h2>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.15fr_0.85fr]">
        <ChatDemoCard />
        <CostBreakdownCard />
      </div>
    </section>
  )
}

function ChatDemoCard() {
  return (
    <div className="rounded-xl border border-border/60 bg-card/50 p-5">
      <div className="mb-4 flex items-center gap-2.5 border-b border-border/40 pb-3.5">
        <span className="inline-flex size-[22px] items-center justify-center rounded bg-[#0a66c2] text-[10px] font-extrabold text-white">
          in
        </span>
        <span className="text-[12px] text-muted-foreground">
          LinkedIn DM, sent from your account
        </span>
        <span className="ml-auto text-[11px] text-muted-foreground/70">
          Jamie Park · Northwind Ventures
        </span>
      </div>

      <div className="flex flex-col gap-2.5">
        <ChatEvent icon={<UserPlus className="size-3" />} text="Connection accepted" />

        <ChatBubble side="us" name="You">
          Hi Jamie, congrats on the Northwind Fund III close. Quick context: I'm raising a $1M
          pre-seed for SeedForge. Pulled together a 4-min walkthrough you can scan before any call.{' '}
          <span className="underline underline-offset-2">seedforge.com/s/seedforge</span>
        </ChatBubble>

        <ChatEvent icon={<Eye className="size-3" />} text="Profile viewed for 4 min 12 sec" />

        <ChatBubble side="them" name="Jamie Park, Founding Partner">
          Read through. Strong founder-market fit. Friday 3pm CET work for a call?
        </ChatBubble>
      </div>
    </div>
  )
}

function ChatEvent({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="mx-auto inline-flex items-center gap-1.5 rounded-full border border-dashed border-border/60 bg-card/30 px-2.5 py-0.5 text-[10.5px] font-medium text-muted-foreground">
      <span className="text-muted-foreground/80">{icon}</span>
      {text}
    </div>
  )
}

function ChatBubble({
  side,
  name,
  children,
}: {
  side: 'us' | 'them'
  name: string
  children: React.ReactNode
}) {
  const isUs = side === 'us'
  return (
    <div className={cn('flex items-start gap-2.5', isUs ? 'flex-row-reverse' : 'flex-row')}>
      <div
        className={cn(
          'flex size-[26px] shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white',
          isUs
            ? 'bg-gradient-to-br from-primary to-[#ea580c]'
            : 'bg-gradient-to-br from-indigo-500 to-violet-700'
        )}
      >
        {isUs
          ? 'YOU'
          : name
              .split(' ')
              .map((w) => w[0])
              .slice(0, 2)
              .join('')}
      </div>
      <div
        className={cn(
          'max-w-[80%] rounded-xl px-3 py-2 text-[13px] leading-relaxed',
          isUs
            ? 'rounded-br-sm bg-primary text-primary-foreground'
            : 'rounded-bl-sm bg-elevated text-foreground'
        )}
      >
        {!isUs && <span className="mb-0.5 block text-[11px] font-semibold opacity-75">{name}</span>}
        {children}
      </div>
    </div>
  )
}

function CostBreakdownCard() {
  return (
    <div className="flex flex-col rounded-xl border border-border/60 bg-card/50 p-5">
      <div className="mb-4 border-b border-border/40 pb-3.5 text-[13px] font-semibold text-foreground">
        Cost breakdown
      </div>

      <CostRow label="Connection request" value="Free" tone="free" />
      <CostRow label="Personalized message" value="Free" tone="free" />
      <CostRow label="Profile view, 4m 12s" value="Free" tone="free" />
      <CostRow label="Call booked" value={`$${PRICING.outreach.bookedCall}`} tone="charge" />

      <div className="mt-2 flex items-center justify-between border-t-2 border-border/60 pt-3.5">
        <span className="text-[13px] font-semibold text-foreground">Cost to meet Jamie</span>
        <span className="text-[20px] font-bold tracking-[-0.02em] text-primary">
          ${PRICING.outreach.bookedCall}
        </span>
      </div>

      <div className="mt-4 flex items-center justify-between gap-2.5 rounded-lg border border-border/40 bg-background/40 px-3 py-3 text-[12.5px] text-muted-foreground">
        <span>See the actual profile Jamie read.</span>
        <a
          href="https://seedforge.com/s/hYoiPikkCoVt"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-1 text-[12.5px] font-semibold text-primary hover:text-primary/80"
        >
          Open
          <ExternalLink className="size-2.5" />
        </a>
      </div>
    </div>
  )
}

function CostRow({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone: 'free' | 'charge'
}) {
  const toneClass: Record<typeof tone, string> = {
    free: 'text-emerald-400 font-semibold',
    charge: 'text-primary font-semibold',
  }
  return (
    <div className="flex items-center justify-between border-b border-border/30 py-2.5 text-[13px] last:border-b-0">
      <span className="text-foreground/85">{label}</span>
      <span className={cn('tabular-nums', toneClass[tone])}>{value}</span>
    </div>
  )
}
